import { useCallback, useContext, useRef, useMemo, useEffect } from 'react';
import { MenuItem, Checkbox, Search, Buttons, Loader } from 'components';
import { ManageCampaignsContext } from 'contexts';
import { AllCampaignsStatusIcon, AllButArchived, FieldOnly } from 'assets/icons';
import * as S from './FiltersBtn.styled';
import { CountryItem } from './CountryItem';
import { useSignal } from '@preact/signals-react';
import { manageAppsAPI } from 'services/manageApps.service';

const chooseIcon = {
  allStatuses: AllCampaignsStatusIcon,
  allButArchived: AllButArchived,
  active: FieldOnly,
  paused: FieldOnly,
  suspended: FieldOnly,
};

const CampaignStatus = ({ isShow, isOpen$ }) => {
  const { filterByStatus, selectedStatusFilter$, statuses } = useContext(ManageCampaignsContext);

  const isStatusFilterSelected = (fieldName) => {
    if (fieldName === 'allStatuses') {
      return fieldName === selectedStatusFilter$.value || selectedStatusFilter$.value === 'empty';
    } else return fieldName === selectedStatusFilter$.value;
  };

  const handleClick = (val) => {
    isOpen$.value = false;
    filterByStatus(val);
  };

  return (
    <S.FilterContentContainer isShow={isShow}>
      {Object.entries(statuses).map(([fieldName, title], index) => {
        const Icon = chooseIcon[fieldName];
        return (
          <MenuItem
            isSelected={isStatusFilterSelected(fieldName)}
            height={40}
            value={fieldName}
            key={index}
            onClick={handleClick}
          >
            <S.Label fieldName={fieldName}>
              <Icon />
              {title}
            </S.Label>
          </MenuItem>
        );
      })}
    </S.FilterContentContainer>
  );
};

const CampaignType = ({ isShow, isOpen$ }) => {
  const { filterByType, selectedTypeFilter$, types } = useContext(ManageCampaignsContext);

  const isTypeFilterSelected = (fieldName) => {
    if (fieldName === 'allTypes') {
      return fieldName === selectedTypeFilter$.value || selectedTypeFilter$.value === 'empty';
    } else {
      return fieldName === selectedTypeFilter$.value;
    }
  };

  const handleClick = (val) => {
    isOpen$.value = false;
    filterByType(val);
  };

  return (
    <S.FilterContentContainer isShow={isShow}>
      {Object.entries(types).map(([fieldName, title], index) => {
        return (
          <MenuItem
            isSelected={isTypeFilterSelected(fieldName)}
            height={40}
            value={fieldName}
            key={index}
            onClick={handleClick}
          >
            <S.Label fieldName={fieldName}>{title}</S.Label>
          </MenuItem>
        );
      })}
    </S.FilterContentContainer>
  );
};

const ApplicationType = ({ isShow, isOpen$, style }) => {
  const { filterByAppType, selectedAppTypeFilter$, appTypes } = useContext(ManageCampaignsContext);

  const handleClick = (val) => {
    isOpen$.value = false;
    filterByAppType(val);
  };

  return (
    <S.FilterContentContainer isShow={isShow} style={style}>
      {Object.entries(appTypes).map(([fieldName, title], index) => {
        return (
          <MenuItem
            isSelected={fieldName === selectedAppTypeFilter$.value}
            height={40}
            value={fieldName}
            key={index}
            onClick={handleClick}
          >
            <S.Label fieldName={fieldName}>{title}</S.Label>
          </MenuItem>
        );
      })}
    </S.FilterContentContainer>
  );
};

const TargetedGeos = ({ isShow, isOpen$, menuClick, style }) => {
  const { isError, isLoading } = manageAppsAPI.useGetCountriesListQuery(undefined, {
    skip: isShow === false,
  });

  const { fullNameCountries, selectedCountriesFilter$, handleApplyClick } =
    useContext(ManageCampaignsContext);

  const searchRef = useRef(null);
  const countrySearch$ = useSignal('');

  useEffect(() => {
    selectedCountries$.value = isShow ? [...selectedCountriesFilter$.value] : [];
  }, [isShow]);

  const selectedCountries$ = useSignal(selectedCountriesFilter$.value);

  const handleClick = (isChecked, cca2) => {
    if (isChecked) {
      selectedCountries$.value = [...selectedCountries$.value, cca2];
    } else {
      selectedCountries$.value = [...selectedCountries$.value.filter((name) => name !== cca2)];
    }
  };

  const handleApplyClickAction = useCallback(() => {
    isOpen$.value = false;
    handleApplyClick(selectedCountries$.value);
    menuClick('campaignStatus');
  }, [selectedCountries$.value]);

  const handleReset = useCallback(() => {
    isOpen$.value = false;
    menuClick('campaignStatus');
  }, [selectedCountries$.value]);

  const filteredCountries = useMemo(() => {
    if (!countrySearch$.value) return fullNameCountries;

    return fullNameCountries?.filter(({ country, cca2 }) => {
      if (country?.toLowerCase().includes(countrySearch$.value.toLowerCase())) return true;
      if (cca2?.toLowerCase().includes(countrySearch$.value.toLowerCase())) return true;
    });
  }, [countrySearch$.value, fullNameCountries]);

  if (isLoading && isShow)
    return (
      <S.CenteredLoader style={style}>
        <Loader.Local delay={0} />
      </S.CenteredLoader>
    );

  if (isError && isShow) return <p style={style}>Something went wrong</p>;

  return (
    <S.TargetContainer isShow={isShow} style={style}>
      <S.SearchContainer>
        <Search
          ref={searchRef}
          placeholder='Search Country'
          value={countrySearch$.value}
          onChange={(e) => (countrySearch$.value = e.target.value)}
          rightIconOnClick={() => {
            countrySearch$.value = '';
          }}
        />
      </S.SearchContainer>
      {!filteredCountries?.length && isShow ? (
        <p style={{ textAlign: 'center', marginTop: '20px' }}>No countries</p>
      ) : (
        <>
          <S.CountriesContent>
            {filteredCountries?.map(({ country, cca2 }) => {
              return (
                <S.CountryItemBox key={cca2}>
                  <Checkbox
                    id={cca2}
                    name={cca2}
                    onChange={(isChecked) => handleClick(isChecked, cca2)}
                    isChecked={selectedCountries$.value.includes(cca2)}
                    label={<CountryItem country={country} cca2={cca2} search$={countrySearch$} />}
                  />
                </S.CountryItemBox>
              );
            })}
          </S.CountriesContent>
          <S.Footer>
            <Buttons.SecondarySmall
              id='reset_filters'
              style={{ marginLeft: 'auto' }}
              onClick={handleReset}
            >
              Cancel
            </Buttons.SecondarySmall>
            <Buttons.PrimarySmall id='apply_filters' onClick={handleApplyClickAction}>
              Apply
            </Buttons.PrimarySmall>
          </S.Footer>
        </>
      )}
    </S.TargetContainer>
  );
};

export const FilterContent = {
  TargetedGeos,
  CampaignType,
  CampaignStatus,
  ApplicationType,
};
